/**
 * Helper functions and predefined view modes for the Persona System
 *
 * Provides convenience functions for creating persona/tool configs
 * and a library of common view modes for reuse.
 */

import { IPersonaConfig, IToolConfig, IViewConfig, IViewMode, ShadingMode } from './types';
import { CameraMode, ClipTask } from '../defines';
import { Viewer } from '../viewer/viewer';
import { PointoramaPlane } from '../utils/PointoramaPlane';
import { setLineTintOnDark } from '../utils/BackdropContrast';
import { getUserDarkModeProperty } from '../../modules/Properties/integration/UserSettingsIntegration';
import { hasGlobalPropertyService } from '../../modules/Properties/reactive';
import * as THREE from 'three';

/** Current theme, from the live `user_dark_mode` reactive property. Falls
 * back to the legacy localStorage/OS check (the property's own default) when
 * the property service isn't initialized yet. */
function isDarkTheme(): boolean {
  if (hasGlobalPropertyService()) return getUserDarkModeProperty().value === true;
  if (typeof localStorage === 'undefined') return false;
  return localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches);
}

/**
 * macOS WebGL fix shared by every X-ray/density view: an invisible transparent
 * plane that must cover the ENTIRE viewport to force the transparent render
 * pass, making density-mode points visible.
 *
 * The plane BILLBOARDS to the active camera each frame. A fixed horizontal
 * plane at z=0 (the previous approach) only covered the viewport in top-down
 * views — side/elevation views (the door/window wall focus) saw it edge-on,
 * so density points only rendered where some other transparent overlay
 * happened to cover the screen.
 */
function addXrayTransparentPlane(viewer: Viewer): void {
  if (!viewer._xrayTransparentPlane) {
    const geometry = new THREE.PlaneGeometry(10000, 10000);
    const material = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0.01,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    viewer._xrayTransparentPlane = new THREE.Mesh(geometry, material);
    viewer._xrayTransparentPlane.renderOrder = -1000; // Render first
  }
  // (Re)apply the billboard behavior every time — the plane instance is
  // cached on the viewer, so a hook set only at creation would be missed by
  // instances that predate it (e.g. across hot reloads).
  const plane = viewer._xrayTransparentPlane;
  // The billboard below runs in onBeforeRender — AFTER culling already
  // happened for the frame — so the plane must never be frustum-culled.
  plane.frustumCulled = false;
  // The onBeforeRender hook owns this matrix (set per frame below) — keep the
  // scene-graph matrix passes from recomposing/overwriting it.
  plane.matrixAutoUpdate = false;
  plane.matrixWorldAutoUpdate = false;
  plane.onBeforeRender = (_renderer, _scene, camera) => {
    // Pin the plane in front of the active camera, facing it, so it spans
    // the viewport regardless of view orientation (top-down, elevation, 3D).
    // Depth: the midpoint of [near, far] — views like the door/window wall
    // elevation clamp the frustum to a thin slab AROUND THE WALL (camera
    // clip override), so any fixed offset from the camera would be clipped
    // away. Perspective cameras (defensive — density views are ortho) keep
    // it near the camera so the finite quad still spans the FOV. Built from
    // camera.matrixWorld so parented cameras resolve correctly. Mutating the
    // matrix here is safe: the renderer computes this object's
    // modelViewMatrix after onBeforeRender returns.
    const cam = camera as THREE.OrthographicCamera | THREE.PerspectiveCamera;
    let distance = (cam.near + cam.far) / 2;
    if ((cam as THREE.PerspectiveCamera).isPerspectiveCamera) {
      distance = Math.min(distance, Math.max(cam.near * 2, cam.near + 10));
    }
    plane.matrixWorld.copy(camera.matrixWorld).multiply(new THREE.Matrix4().makeTranslation(0, 0, -distance));
  };
  viewer.scene.scene.add(plane);
}

/**
 * Helper to create a persona configuration with defaults
 *
 * @param config - Partial persona config
 * @returns Complete persona config with defaults applied
 *
 * @example
 * const personaConfig = createPersonaConfig({
 *   name: 'annotation',
 *   priority: 10,
 *   exclusive: true,
 *   tools: [measurePointConfig, measureDistanceConfig],
 * });
 */
export function createPersonaConfig(config: IPersonaConfig): IPersonaConfig {
  return {
    priority: 0,
    exclusive: false,
    ...config,
  };
}

/**
 * Helper to create a tool configuration with defaults
 *
 * @param config - Tool config
 * @returns Tool config with defaults applied
 *
 * @example
 * const toolConfig = createToolConfig({
 *   name: 'measure_point',
 *   factory: (viewer, persona) => new MeasurePointTool(viewer, persona),
 *   exclusive: true,
 * });
 */
export function createToolConfig(config: IToolConfig): IToolConfig {
  return {
    defaultEnabled: false,
    exclusive: false,
    ...config,
  };
}

/**
 * Helper to create a view mode with defaults
 *
 * @param config - Partial view mode config (id and name required)
 * @returns Complete view mode
 *
 * @example
 * const customView = createViewMode({
 *   id: 'my_custom_view',
 *   name: 'Custom View',
 *   cameraMode: CameraMode.PERSPECTIVE,
 *   shadingMode: ShadingMode.HEIGHT,
 * });
 */
export function createViewMode(config: Partial<IViewMode> & { id: string; name: string }): IViewMode {
  return {
    description: '',
    ...config,
  };
}

/**
 * Predefined view modes for common scenarios
 *
 * These can be used directly or as templates for custom views.
 * Each view mode is optimized for specific use cases.
 */
export const ViewModes = {
  /**
   * Standard 3D perspective view with RGB coloring
   * Best for: General navigation, RGB visualization
   */
  PERSPECTIVE_3D_RGB: createViewMode({
    id: 'perspective_3d_rgb',
    name: '3D',
    description: 'Standard 3D view with RGB coloring',
    cameraMode: CameraMode.PERSPECTIVE,
    cameraProfile: 'perspective_3d',
    shadingMode: ShadingMode.RGB,
    edlEnabled: true,
    edlStrength: 0.8,
    mapVisible: false,
    gridVisible: false,
    icon: '3d',
    // NOTE: No onEnter - ViewManager.applyViewContext handles disable2DMode() for 3D views
  }),

  /**
   * Top-down orthographic view
   * Best for: Plan views, 2D measurements, floorplans
   *
   * Note: No onEnter hook - ViewManager.applyViewContext handles all 2D mode setup.
   * This prevents conflicts when switching to plane-specific views via viewContext.
   */
  ORTHOGRAPHIC_2D_TOP: createViewMode({
    id: 'orthographic_2d_top',
    name: '2D',
    description: 'Top-down orthographic view',
    cameraMode: CameraMode.ORTHOGRAPHIC,
    cameraProfile: 'top_down_2d',
    shadingMode: ShadingMode.RGB,
    edlEnabled: false,
    mapVisible: false,
    gridVisible: true,
    icon: '2d',
    // NOTE: No onExit - ViewManager handles mode transitions
    // Calling disable2DMode() here would break 2D->2D view transitions
  }),

  /**
   * Side orthographic view (profile view)
   * Best for: Floor/ceiling detection, elevation analysis, building sections
   *
   * Shows building from the side (Y axis) with Z as up
   */
  ORTHOGRAPHIC_2D_SIDE: createViewMode({
    id: 'orthographic_2d_side',
    name: '2D Side',
    description: 'Side profile orthographic view',
    cameraMode: CameraMode.ORTHOGRAPHIC,
    cameraProfile: 'side_2d',
    shadingMode: ShadingMode.RGB,
    edlEnabled: false,
    mapVisible: false,
    gridVisible: true,
    icon: 'side-view',
    onEnter: async (viewer: Viewer) => {
      // Calculate camera position for side view using scene bounding box
      const boundingBox = viewer.getBoundingBox(viewer.scene.pointclouds);

      const center = new THREE.Vector3();
      boundingBox.getCenter(center);
      const size = new THREE.Vector3();
      boundingBox.getSize(size);

      // Create a virtual plane for the side view (perpendicular to Y axis)
      const normal = new THREE.Vector3(0, 1, 0); // Normal pointing along Y axis
      const position = center.clone();

      // Enable 2D mode to lock camera rotation
      // EDL is suppressed by ViewManager.applyViewContext after this hook returns.
      viewer.setCameraMode(CameraMode.ORTHOGRAPHIC);
      viewer.viewerMode.in2DMode = true;
      viewer.viewerMode.associatedPlane = new PointoramaPlane({
        position,
        normal,
        clipTask: ClipTask.NONE,
      });
      viewer.viewerMode.hasVirtualPlane = true;

      // Position camera to look from the side at the center
      const view = viewer.scene.view;

      // Set pitch, roll, and yaw for side view
      // pitch: 0 (looking horizontally)
      // yaw: π (180 degrees, looking from +Y towards -Y)
      view.pitch = 0;
      view.roll = 0;
      view.yaw = Math.PI;

      // Position camera along the +Y axis, offset from center
      view.position.set(center.x, center.y + size.y, center.z);

      // Set direction to look towards center
      const direction = new THREE.Vector3(0, -1, 0); // Looking towards -Y
      view.direction = direction;

      // Set radius to distance from camera to center
      view.radius = size.y;
    },
    // NOTE: No onExit - ViewManager handles mode transitions
    // Calling disable2DMode() here would break 2D->2D view transitions
  }),

  /**
   * X-Ray density heatmap view (top-down, restores saved yaw)
   * Best for: Point density visualization, coverage analysis, map views
   *
   * Uses density_mode shader to render white points with alpha blending
   * to create a heatmap effect showing point cloud density.
   * This view restores saved camera yaw for consistent map orientation.
   */
  XRAY_2D: createViewMode({
    id: 'xray_2d',
    name: 'X-Ray',
    description: 'Density heatmap visualization',
    cameraMode: CameraMode.ORTHOGRAPHIC,
    cameraProfile: 'top_down_2d',
    shadingMode: ShadingMode.RGB,
    edlEnabled: false,
    mapVisible: false,
    gridVisible: true,
    icon: 'xray',
    onEnter: async (viewer: Viewer) => {
      // NOTE: Camera positioning is handled by ViewManager.applyViewContext()
      // Don't call enable2DMode() here as it would override camera for 2D->2D transitions

      // Enable density mode for all point clouds
      viewer.scene.pointclouds.forEach((pointCloud) => {
        pointCloud.material.densityMode = true;
      });

      // HACK: macOS WebGL fix — invisible camera-facing transparent plane
      // covering the viewport so the transparent pass (density points) renders.
      addXrayTransparentPlane(viewer);

      // Set background to pitch black for xray view
      viewer.setBackground('black');
    },
    onExit: async (viewer: Viewer) => {
      // NOTE: Don't call disable2DMode() here - ViewManager handles mode transitions
      // Calling it here would break 2D->2D view transitions

      // Disable density mode for all point clouds
      viewer.scene.pointclouds.forEach((pointCloud) => {
        pointCloud.material.densityMode = false;
      });

      // Remove the transparent plane hack
      if (viewer._xrayTransparentPlane) {
        viewer.scene.scene.remove(viewer._xrayTransparentPlane);
      }

      // Restore theme-matched background
      if (typeof localStorage !== 'undefined') {
        if (localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
          viewer.setBackground('dark');
        } else {
          viewer.setBackground('light');
        }
      }
    },
  }),

  /**
   * X-Ray density heatmap view (surface/side, does NOT restore saved yaw)
   * Best for: Surface analysis tools, side views, plane-aligned views
   *
   * Uses density_mode shader to render white points with alpha blending
   * to create a heatmap effect showing point cloud density.
   * Unlike XRAY_2D, this view does NOT restore saved camera yaw,
   * preserving the current view orientation for surface/plane work.
   */
  XRAY_2D_SURFACE: createViewMode({
    id: 'xray_2d_surface',
    name: 'X-Ray',
    description: 'Density heatmap for surface analysis',
    cameraMode: CameraMode.ORTHOGRAPHIC,
    cameraProfile: 'plane_2d',
    shadingMode: ShadingMode.RGB,
    edlEnabled: false,
    mapVisible: false,
    gridVisible: true,
    icon: 'xray',
    onEnter: async (viewer: Viewer) => {
      // NOTE: Camera orientation is handled by the tool (e.g., SurfaceToolBase.alignViewToSurface)
      // We only set the 2D mode flag here - don't call enable2DMode() which would override the camera
      viewer.viewerMode.in2DMode = true;

      // Set camera to orthographic mode
      // EDL is suppressed by ViewManager.applyViewContext after this hook returns.
      viewer.setCameraMode(CameraMode.ORTHOGRAPHIC);

      // Enable density mode for all point clouds
      viewer.scene.pointclouds.forEach((pointCloud) => {
        pointCloud.material.densityMode = true;
      });

      // HACK: macOS WebGL fix — invisible camera-facing transparent plane
      // covering the viewport so the transparent pass (density points) renders.
      addXrayTransparentPlane(viewer);

      // Set background to pitch black for xray view
      viewer.setBackground('black');
    },
    onExit: async (viewer: Viewer) => {
      // NOTE: Don't call disable2DMode() here - ViewManager handles mode transitions
      // Calling it here would break 2D->2D view transitions

      // Disable density mode for all point clouds
      viewer.scene.pointclouds.forEach((pointCloud) => {
        pointCloud.material.densityMode = false;
      });

      // Remove the transparent plane hack
      if (viewer._xrayTransparentPlane) {
        viewer.scene.scene.remove(viewer._xrayTransparentPlane);
      }

      // Restore theme-matched background
      if (typeof localStorage !== 'undefined') {
        if (localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
          viewer.setBackground('dark');
        } else {
          viewer.setBackground('light');
        }
      }
    },
  }),

  /**
   * X-Ray density heatmap view aligned to a wall (perpendicular to the wall
   * surface). Tools that activate this view (door/window editing) are
   * responsible for calling `alignViewToWall` to position the camera; this
   * mode only sets the global flags (ortho + xray + density). Mirrors
   * `XRAY_2D_SURFACE` in spirit — view shell, no camera pose.
   */
  XRAY_2D_WALL: createViewMode({
    id: 'xray_2d_wall',
    name: 'Wall',
    description: 'Wall-perpendicular X-Ray view for door/window editing',
    cameraMode: CameraMode.ORTHOGRAPHIC,
    cameraProfile: 'plane_2d',
    shadingMode: ShadingMode.RGB,
    edlEnabled: false,
    mapVisible: false,
    gridVisible: false,
    icon: 'xray',
    onEnter: async (viewer: Viewer) => {
      // Camera pose is set by the activating tool's alignViewToWall call.
      viewer.viewerMode.in2DMode = true;
      viewer.setCameraMode(CameraMode.ORTHOGRAPHIC);

      // Enable density mode for all point clouds (same as the other xray views).
      viewer.scene.pointclouds.forEach((pointCloud) => {
        pointCloud.material.densityMode = true;
      });

      // macOS WebGL transparent-pass fix (same as XRAY_2D / XRAY_2D_SURFACE).
      addXrayTransparentPlane(viewer);

      viewer.setBackground('black');
    },
    onExit: async (viewer: Viewer) => {
      viewer.scene.pointclouds.forEach((pointCloud) => {
        pointCloud.material.densityMode = false;
      });

      if (viewer._xrayTransparentPlane) {
        viewer.scene.scene.remove(viewer._xrayTransparentPlane);
      }

      if (typeof localStorage !== 'undefined') {
        if (localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
          viewer.setBackground('dark');
        } else {
          viewer.setBackground('light');
        }
      }
    },
  }),

  /**
   * Plan view: a clean top-down floor plan showing ONLY the skeleton (2D wall
   * skinning, door/window glyphs, outlines) on a theme-matched background —
   * white in light theme, black with white glyphs in dark theme — point
   * clouds are hidden entirely. Like the X-ray views in spirit (top-down
   * orthographic, ViewManager handles camera pose) but flattened to a printable
   * floor plan instead of a density heatmap.
   *
   * Forces every skeleton into flat 2D skinning mode on enter and restores the
   * prior 2D/3D state on exit.
   */
  PLAN_2D_SKELETON: createViewMode({
    id: 'plan_2d_skeleton',
    name: 'Plan',
    description: 'Floor plan — skeleton only',
    cameraMode: CameraMode.ORTHOGRAPHIC,
    cameraProfile: 'top_down_2d',
    shadingMode: ShadingMode.RGB,
    edlEnabled: false,
    mapVisible: false,
    gridVisible: false,
    icon: 'plan',
    onEnter: async (viewer: Viewer) => {
      // Top-down orthographic; camera pose is handled by
      // ViewManager.applyViewContext after this hook (same as XRAY_2D).
      viewer.viewerMode.in2DMode = true;
      viewer.setCameraMode(CameraMode.ORTHOGRAPHIC);

      // Hide the point cloud so only the skeleton skin remains.
      viewer.scene.pointclouds.forEach((pointCloud) => {
        pointCloud.visible = false;
      });

      // Flatten every skeleton to 2D skinning, remembering the prior mode so we
      // can restore it on exit. The skinning group renders independently of the
      // point clouds, so it stays visible.
      viewer._planViewPrevSkinning3D = viewer.scene.wallSketches.map((s) => s.skinning3D);
      viewer.scene.wallSketches.forEach((skeleton) => {
        skeleton.skinning3D = false;
      });

      // Print-style plan: white paper in light theme, inverted (black paper,
      // white glyphs) in dark theme. The backdrop-invert shader resolves line
      // color from the backdrop, so on dark we also swap its bright tint from
      // the brand purple (the X-Ray look) to plain white. Subscribing to the
      // live dark-mode property re-inverts the open plan the moment the user
      // toggles the theme.
      const applyPlanTheme = (dark: boolean): void => {
        viewer.setBackground(dark ? 'black' : 'white');
        setLineTintOnDark(dark ? '#ffffff' : undefined);
      };
      applyPlanTheme(isDarkTheme());
      if (hasGlobalPropertyService()) {
        viewer._planViewThemeSubscription = getUserDarkModeProperty().subscribeToChanges((dark) => applyPlanTheme(dark === true));
      }
    },
    onExit: async (viewer: Viewer) => {
      // Restore point cloud visibility.
      viewer.scene.pointclouds.forEach((pointCloud) => {
        pointCloud.visible = true;
      });

      // Restore each skeleton's prior 2D/3D skinning state.
      const prev = viewer._planViewPrevSkinning3D;
      if (prev) {
        viewer.scene.wallSketches.forEach((skeleton, i) => {
          if (i < prev.length) skeleton.skinning3D = prev[i];
        });
        viewer._planViewPrevSkinning3D = undefined;
      }

      // Restore the brand line tint and theme-matched background (same
      // pattern as the X-ray views).
      viewer._planViewThemeSubscription?.unsubscribe();
      viewer._planViewThemeSubscription = undefined;
      setLineTintOnDark();
      viewer.setBackground(isDarkTheme() ? 'dark' : 'light');
    },
  }),
};

/**
 * Helper to create a view config with multiple views
 *
 * @param views - Array of view modes
 * @param defaultViewId - Optional default view ID (uses first if not specified)
 * @returns View configuration
 *
 * @example
 * const viewConfig = createMultiViewConfig([
 *   ViewModes.PERSPECTIVE_3D_RGB,
 *   ViewModes.ORTHOGRAPHIC_2D_TOP,
 *   ViewModes.PERSPECTIVE_3D_HEIGHT,
 * ], 'perspective_3d_rgb');
 */
export function createMultiViewConfig(views: IViewMode[], defaultViewId?: string): IViewConfig {
  return {
    views,
    defaultViewId: defaultViewId || views[0]?.id,
  };
}

/**
 * Helper to get views from config
 * Handles undefined/empty configs gracefully
 *
 * @param config - View config (optional)
 * @returns Array of view modes (empty array if none)
 *
 * @example
 * const views = getViewsFromConfig(tool.getViewConfig());
 */
export function getViewsFromConfig(config?: IViewConfig): IViewMode[] {
  return config?.views || [];
}

/**
 * Helper to merge view modes from multiple arrays
 * Later view modes override earlier ones with same ID
 *
 * Useful for merging persona views with tool views.
 *
 * @param viewArrays - Multiple arrays of view modes
 * @returns Merged array of unique view modes
 *
 * @example
 * const merged = mergeViewModes(
 *   [personaView1, personaView2],
 *   [toolView1, toolView2] // toolView1 overrides personaView1 if same ID
 * );
 */
export function mergeViewModes(...viewArrays: IViewMode[][]): IViewMode[] {
  const viewMap = new Map<string, IViewMode>();

  for (const views of viewArrays) {
    for (const view of views) {
      viewMap.set(view.id, view);
    }
  }

  return Array.from(viewMap.values());
}

/**
 * Helper to find a view mode by ID
 *
 * @param views - Array of view modes
 * @param viewId - View mode ID to find
 * @returns View mode or undefined
 *
 * @example
 * const view = findViewById(availableViews, 'perspective_3d_rgb');
 */
export function findViewById(views: IViewMode[], viewId: string): IViewMode | undefined {
  return views.find((v) => v.id === viewId);
}

/**
 * Helper to check if view config represents a simple 2D/3D toggle
 * Used by UI to decide between toggle button vs dropdown
 *
 * @param config - View config
 * @returns True if config is 2D/3D toggle scenario
 *
 * @example
 * const isToggle = isSimple2D3DToggle(viewConfig);
 * // Shows toggle button instead of dropdown
 */
export function isSimple2D3DToggle(config: IViewConfig): boolean {
  const views = config.views || [];

  if (views.length !== 2) return false;

  const hasPerspective = views.some((v) => v.cameraMode === CameraMode.PERSPECTIVE);
  const hasOrthographic = views.some((v) => v.cameraMode === CameraMode.ORTHOGRAPHIC);

  return hasPerspective && hasOrthographic;
}

/**
 * Helper to get default view ID from config
 *
 * @param config - View config
 * @returns Default view ID or undefined
 *
 * @example
 * const defaultId = getDefaultViewId(viewConfig);
 */
export function getDefaultViewId(config: IViewConfig): string | undefined {
  if (config.defaultViewId) return config.defaultViewId;
  if (config.views && config.views.length > 0) return config.views[0].id;
  return undefined;
}

/**
 * Predefined view config combinations for common use cases
 */
export const ViewConfigPresets = {
  /**
   * Single 3D perspective view (default)
   */
  PERSPECTIVE_3D_ONLY: createMultiViewConfig([ViewModes.PERSPECTIVE_3D_RGB]),

  /**
   * Simple 2D/3D toggle
   * UI will show toggle button
   */
  SIMPLE_2D_3D_TOGGLE: createMultiViewConfig([ViewModes.PERSPECTIVE_3D_RGB, ViewModes.ORTHOGRAPHIC_2D_TOP], 'perspective_3d_rgb'),
};
